import React from 'react';
import { useNavigate } from 'react-router-dom';

import type { GameDto } from '../../api/types';

import { FavoriteButton } from './FavoriteButton';
import { GameTagsList } from './GameTagsList';

import './GameCard.css';

type GameCardProps = {
  game: GameDto;
  activeTags?: string[];
  onTagClick?: (tag: string) => void;
  /** When true, card is non-navigable and shows no favorite button (for embedding in other cards) */
  embedded?: boolean;
  collapsibleTags?: boolean;
};

export function GameCard({ game, activeTags = [], onTagClick, embedded = false, collapsibleTags = false }: GameCardProps) {
  const navigate = useNavigate();

  const handleClick = embedded
    ? undefined
    : () => { navigate(`/games/${game.id}`); };

  const tagNames = game.tags.map((t) => t.name);

  const handleTagClick = onTagClick
    ? (tag: string) => {
        onTagClick(tag);
      }
    : undefined;

  return (
    <div
      className={`game-card${embedded ? ' game-card--embedded' : ''}`}
      onClick={handleClick}
      style={embedded ? { cursor: 'default' } : undefined}
    >
      <div className="game-card__image-wrap">
        {game.imageUrl ? (
          <img className="game-card__image" src={game.imageUrl} alt={game.name} />
        ) : (
          <div className="game-card__image-placeholder">🎲</div>
        )}
      </div>

      <div className="game-card__body">
        <div className="game-card__header">
          <div className="game-card__title">{game.name}</div>
          {!embedded && (
            <div className="game-card__favorite">
              <FavoriteButton gameId={game.id} />
            </div>
          )}
        </div>

        <div className="game-card__meta">
          <span className="game-card__meta-item">👥 {game.minPlayers}–{game.maxPlayers}</span>
          {game.estimatedDuration != null && (
            <span className="game-card__meta-item">⏱ ~{game.estimatedDuration} мин.</span>
          )}
        </div>

        {tagNames.length > 0 && (
          <div
            className="game-card__tags"
            onClick={(e) => {
              if (handleTagClick || collapsibleTags) e.stopPropagation();
            }}
          >
            <GameTagsList
              tags={tagNames}
              compact
              mode={handleTagClick ? 'filter' : 'display'}
              activeTags={activeTags}
              onTagClick={handleTagClick}
              collapsible={collapsibleTags}
            />
          </div>
        )}
      </div>
    </div>
  );
}
